// FreeBookFormPage — the free-book claim form. A request lands in the Engine's
// book ledger as a book owed (thread mct_bdaba308-bee; worker books-free.ts).
//
//   /free-book/:region                       — standalone page in the TSB shell (na | au)
//   /free-book/:region?embed=1&page=<slug>   — the form alone, for an iframe on a
//                                              free-book page; `page` names that page
//                                              (friends-of-michael, accounting-finance-show,
//                                              friends-of-debbie). It reports its own height
//                                              to the parent page so the frame grows with it.
//
// Fields, choices and wording come from the engine (GET /api/public/books/free/:region).
// NA asks two business questions as well as the address; AU asks the address only,
// matching the HubSpot forms they replace.
//
// Printed copies go only to the US, Canada, Australia and the UK. "Another country"
// hides the postal address, asks which country, and the thank-you says plainly that
// it will be the ebook (Michael's rule, 11 Oct 2026).

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface Choice { value: string; label: string }
interface Form {
  region: string; heading: string; intro: string; countryLabel: string; countryHelp: string; countries: Choice[]; otherCountryValue: string;
  businessStages: Choice[] | null; describesYou: Choice[] | null; submitLabel: string; thanksHeading: string; thanksBody: string;
}

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.15, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 24px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)' };
const label: CSSProperties = { display: 'block', fontWeight: 600, fontSize: 15, color: '#0A0A2E', marginBottom: 6 };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '12px 14px', borderRadius: 10, border: '1px solid #d6d3cc', background: '#fff', fontFamily: 'inherit' };
const btn: CSSProperties = { background: '#F05A28', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer' };
const row: CSSProperties = { marginBottom: 16 };
const help: CSSProperties = { fontSize: 13, color: '#6b6b80', margin: '-2px 0 6px' };
const grid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 14px' };

type TextKey = 'firstName' | 'lastName' | 'email' | 'company' | 'phone' | 'address1' | 'address2' | 'city' | 'state' | 'postalCode' | 'countryOther';

export default function FreeBookFormPage() {
  // Mounted by FreeBookPage under /free-book/:book for the two legacy region aliases.
  const { book: region = '' } = useParams();
  const [params] = useSearchParams();
  const embed = params.get('embed') === '1';
  const page = params.get('page') ?? '';
  const [form, setForm] = useState<Form | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<{ heading: string; body: string } | null>(null);
  const started = useRef(Date.now());
  const hp = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${API}/api/public/books/free/${encodeURIComponent(region)}`)
      .then(async (r) => {
        if (r.status === 404) { setState('missing'); return; }
        if (!r.ok) { setState('error'); return; }
        setForm((await r.json()).form as Form);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [region]);

  // Tell the parent page how tall we are, so its iframe can fit the form.
  useEffect(() => {
    if (!embed || !root.current || typeof ResizeObserver === 'undefined') return;
    const post = () => window.parent?.postMessage({ type: 'tsb-free-book-form-height', region, height: Math.ceil(root.current!.getBoundingClientRect().height) + 4 }, '*');
    const ro = new ResizeObserver(post);
    ro.observe(root.current);
    post();
    return () => ro.disconnect();
  }, [embed, region, state, done]);

  const set = (k: string, v: string) => setValues((cur) => ({ ...cur, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setBusy(true); setErr('');
    try {
      const pageUrl = embed ? (document.referrer || '') : window.location.href;
      const r = await fetch(`${API}/api/public/books/free/${encodeURIComponent(region)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, page, pageUrl, hp: hp.current?.value ?? '', formMs: Date.now() - started.current }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d?.error ?? `Something went wrong (${r.status}). Please try again.`); return; }
      setDone({ heading: d.thanksHeading ?? form.thanksHeading, body: d.thanksBody ?? form.thanksBody });
      if (!embed) window.scrollTo(0, 0);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  const text = (k: TextKey, lbl: string, opts: { required?: boolean; type?: string; auto?: string; max?: number } = {}) => (
    <div style={row}>
      <label htmlFor={`fb-${k}`} style={label}>{lbl}{opts.required === false ? '' : ' *'}</label>
      <input id={`fb-${k}`} type={opts.type ?? 'text'} required={opts.required !== false} autoComplete={opts.auto} maxLength={opts.max ?? 200} style={input}
        value={values[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
    </div>
  );
  const select = (k: string, lbl: string, choices: Choice[], helpText?: string) => (
    <div style={row}>
      <label htmlFor={`fb-${k}`} style={label}>{lbl} *</label>
      {helpText && <p style={help}>{helpText}</p>}
      <select id={`fb-${k}`} required style={input} value={values[k] ?? ''} onChange={(e) => set(k, e.target.value)}>
        <option value="" disabled>Please select</option>
        {choices.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  let body: React.ReactNode;
  if (state === 'loading') body = <p style={lead}>Loading…</p>;
  else if (state === 'missing') body = <p style={lead}>We could not find that form.</p>;
  else if (state === 'error' || !form) body = <p style={lead}>Something went wrong loading this form. Please refresh the page, or email support@thesuccessfulbookkeeper.com.</p>;
  else if (done) {
    body = (
      <div style={card} role="status">
        <p style={{ ...lead, margin: '0 0 8px', color: '#0A0A2E' }}><strong>{done.heading}</strong></p>
        <p style={{ ...lead, margin: 0 }}>{done.body}</p>
      </div>
    );
  } else {
    body = (
      <>
        {!embed && <><h1 style={h1}>{form.heading}</h1><p style={lead}>{form.intro}</p></>}
        <form onSubmit={submit} style={embed ? undefined : card}>
          <div style={grid}>
            {text('firstName', 'First name', { auto: 'given-name', max: 80 })}
            {text('lastName', 'Last name', { auto: 'family-name', max: 80 })}
          </div>
          {text('email', 'Email', { type: 'email', auto: 'email' })}
          {text('company', 'Company name', { auto: 'organization', max: 160 })}
          {select('country', form.countryLabel, form.countries, form.countryHelp)}
          {values.country === form.otherCountryValue ? (
            text('countryOther', 'Which country are you in?', { auto: 'country-name', max: 80 })
          ) : (
            <>
              {text('address1', 'Street address', { auto: 'address-line1' })}
              {text('address2', 'Street address 2', { required: false, auto: 'address-line2' })}
              <div style={grid}>
                {text('city', 'City', { auto: 'address-level2', max: 100 })}
                {text('state', 'State / Region', { auto: 'address-level1', max: 100 })}
              </div>
              <div style={grid}>
                {text('postalCode', 'Postal code', { auto: 'postal-code', max: 20 })}
                {text('phone', 'Phone number', { type: 'tel', auto: 'tel', max: 40 })}
              </div>
            </>
          )}
          {form.businessStages && select('businessStage', 'What is the current stage of your bookkeeping business?', form.businessStages)}
          {form.describesYou && select('describesYou', 'Which of these statements describes you best right now?', form.describesYou)}
          {/* Honeypot: hidden from people, filled by bots. */}
          <div aria-hidden="true" style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>
            <label htmlFor="fb-website">Website</label>
            <input id="fb-website" ref={hp} tabIndex={-1} autoComplete="off" name="website" />
          </div>
          <button type="submit" disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>{busy ? 'Sending…' : form.submitLabel}</button>
          {err && <p role="alert" style={{ color: '#B42318', fontSize: 15, margin: '12px 0 0' }}>{err}</p>}
        </form>
      </>
    );
  }

  if (embed) {
    return (
      <div ref={root} style={{ fontFamily: "'Inter', 'Poppins', Arial, sans-serif", padding: '4px 2px', background: 'transparent' }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap'); body{margin:0;background:transparent}`}</style>
        {body}
      </div>
    );
  }
  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div ref={root} style={{ maxWidth: 640, margin: '0 auto', padding: '44px 16px 80px' }}>{body}</div>
    </TakerShell>
  );
}
